import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { MATERIALS, MATERIAL_BY_NAME } from '@/lib/planMaterials';
import { guidePrice, priceUnit } from '@/lib/planPricing';
import PriceListImport from '@/components/PriceListImport';

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

// Settings → Price list: one supply & install price per plan material, shared by every device
export default function PriceListSection({ user, refresh }) {
  const [prices, setPrices] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPrices(Object.fromEntries(Object.entries(user?.plan_prices || {}).map(([k, v]) => [k, String(v)]))); }, [user]);
  const cats = [...new Set(MATERIALS.map((m) => m.category))];
  const extras = Object.keys(prices).filter((k) => !MATERIAL_BY_NAME[k]).sort();

  const save = async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(prices).filter(([, v]) => v !== '' && !Number.isNaN(+v)).map(([k, v]) => [k, +v]));
      await api.put('/me/plan-prices', { prices: clean });
      await refresh(); toast.success('Price list saved');
    } catch { toast.error('Could not save price list'); } finally { setBusy(false); }
  };
  const removeExtra = (k) => { const next = { ...prices }; delete next[k]; setPrices(next); };

  return (
    <div className="tm-card p-6 space-y-5" data-testid="settings-price-list">
      <div>
        <h2 className="font-display text-2xl font-semibold">Supply &amp; install price list</h2>
        <p className="text-sm text-slate-500 mt-1">Your own prices for every Plan Designer material. “Turn into quote” uses these on every device. Leave blank to use the guide price shown in grey.</p>
      </div>
      <PriceListImport refresh={refresh} />
      {cats.map((c) => (
        <section key={c}>
          <p className="tm-label">{c}</p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {MATERIALS.filter((m) => m.category === c).map((m) => (
              <label key={m.key} className="flex items-center gap-3 text-sm" data-testid={`price-row-${m.key}`}>
                <span className="flex-1 truncate">{m.name}</span>
                <span className="text-slate-400">£</span>
                <input type="number" min="0" step="any" value={prices[m.name] ?? ''} placeholder={String(guidePrice(m.name))}
                  onChange={(e) => setPrices({ ...prices, [m.name]: e.target.value })} className="tm-input w-24 min-h-0 py-1.5" data-testid={`price-input-${m.key}`} />
                <span className="w-10 text-xs text-slate-500">/ {priceUnit(m.name)}</span>
              </label>
            ))}
          </div>
        </section>
      ))}
      {extras.length > 0 && (
        <section data-testid="price-extras">
          <p className="tm-label">Other supplier items ({extras.length})</p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {extras.map((k) => (
              <div key={k} className="flex items-center gap-3 text-sm" data-testid={`price-extra-${slug(k)}`}>
                <span className="flex-1 truncate" title={k}>{k}</span>
                <span className="text-slate-400">£</span>
                <input type="number" min="0" step="any" value={prices[k]} onChange={(e) => setPrices({ ...prices, [k]: e.target.value })}
                  className="tm-input w-24 min-h-0 py-1.5" data-testid={`price-extra-input-${slug(k)}`} />
                <span className="w-10 text-xs text-slate-500 truncate">/ {user?.price_units?.[k] || 'item'}</span>
                <button type="button" onClick={() => removeExtra(k)} className="text-slate-400 hover:text-rose-600" aria-label={`Remove ${k}`} data-testid={`price-extra-remove-${slug(k)}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </section>
      )}
      <button type="button" onClick={save} disabled={busy} className="tm-btn" data-testid="price-list-save">{busy ? 'Saving…' : 'Save price list'}</button>
    </div>
  );
}
